import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Compass, 
  Target, 
  TrendingUp, 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Users,
  MapPin,
  Briefcase,
  Clock,
  Award,
  BarChart3,
  Loader2,
  FileText,
  AlertTriangle,
  Info,
  XCircle,
  Building,
  DollarSign,
  Search,
  Lightbulb,
  Route
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { CareerMatchIntake } from "@/components/CareerMatchIntake";
import { CareerMatchResults } from "@/components/CareerMatchResults";
import { CareerMatchService } from "@/services/careerMatchService";
import { IntakeFormData, CareerMatchReport } from "@/types/careerMatch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

export default function CareerClarityTool() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<'landing' | 'intake' | 'processing' | 'results'>('landing');
  const [currentReport, setCurrentReport] = useState<CareerMatchReport | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Legacy state for backward compatibility
  const [currentExperience, setCurrentExperience] = useState("");
  const [skills, setSkills] = useState("");
  const [goals, setGoals] = useState("");
  const [location, setLocation] = useState("");
  const [experience, setExperience] = useState("");
  
  const { toast } = useToast();
  const { user } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStartAssessment = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    setCurrentStep('intake');
  };

  const handleQuickStart = () => {
    if (!currentExperience || !skills || !goals) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields to begin your career clarity assessment.",
        variant: "destructive",
      });
      return;
    }
    
    // Convert legacy form to intake data for quick start
    const quickIntakeData: IntakeFormData = {
      resumeText: `Experience: ${currentExperience}\nSkills: ${skills}\nGoals: ${goals}`,
      coverLetterText: '',
      portfolioLinks: [],
      notes: '',
      city: location.split(',')[0] || '',
      state: location.split(',')[1]?.trim() || '',
      searchRadius: 25,
      workSetup: 'hybrid',
      salaryTarget: 80000,
      salaryMinimum: 60000,
      industriesToInclude: [],
      industriesToExclude: [],
      companySize: 'any',
      totalYearsExperience: parseInt(experience) || 5,
      domainSpecificExperience: parseInt(experience) || 3,
      managementExperience: false,
      toolsAndPlatforms: skills.split(',').map(s => s.trim()),
      certifications: [],
      educationLevel: "Bachelor's Degree",
      educationMajor: '',
      careerWin1: '',
      careerWin2: '',
      constraint1: '',
      constraint2: '',
      seedRole1: currentExperience,
      seedRole2: '',
      seedRole3: '',
      workType: 'permanent'
    };
    
    handleIntakeComplete(quickIntakeData);
  };

  const handleIntakeComplete = async (intakeData: IntakeFormData) => {
    if (!user) return;
    
    setIsProcessing(true);
    setCurrentStep('processing');
    
    try {
      // Create the report first
      const { report, error: createError } = await CareerMatchService.createReport(
        user.id,
        intakeData,
        'Career Match Report'
      );
      
      if (createError || !report) {
        throw new Error(createError?.message || 'Failed to create report');
      }
      
      // Generate role matches
      const { roleMatches, error: generateError } = await CareerMatchService.generateReport(intakeData);
      
      if (generateError) {
        throw new Error(generateError.message);
      }
      
      // Update report with results
      const { error: updateError } = await CareerMatchService.updateReportWithMatches(
        report.id,
        roleMatches,
        'completed'
      );
      
      if (updateError) {
        throw new Error(updateError.message);
      }
      
      // Set completed report
      setCurrentReport({
        ...report,
        role_matches: roleMatches,
        status: 'completed'
      });
      
      setCurrentStep('results');
      
      toast({
        title: "Report Generated!",
        description: `Found ${roleMatches.length} potential career matches for you.`,
      });
      
    } catch (error: any) {
      console.error('Error generating career match report:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate your career match report. Please try again.",
        variant: "destructive",
      });
      setCurrentStep('intake');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToLanding = () => {
    setCurrentStep('landing');
    setCurrentReport(null);
  };

  // Render different steps
  if (currentStep === 'intake') {
    return (
      <CareerMatchIntake 
        onComplete={handleIntakeComplete}
        onBack={handleBackToLanding}
      />
    );
  }

  if (currentStep === 'processing') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Analyzing Your Profile</h2>
            <p className="text-muted-foreground mb-4">
              We're matching your skills and experience with current market opportunities. 
              This usually takes 2-3 minutes.
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Analyzing resume and skills</p>
              <p>✓ Researching salary data</p>
              <p>✓ Checking market demand</p>
              <p>📊 Calculating fit scores...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'results' && currentReport) {
    return (
      <CareerMatchResults 
        report={currentReport}
        onBack={handleBackToLanding}
        onRegenerateWithFilters={() => setCurrentStep('intake')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-blue-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-9 w-9 p-0 bg-white">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <Compass className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">JobIntel 360 Career Clarity Report</h1>
              <p className="text-muted-foreground mt-1">
                Your personalized career roadmap built on verified data!
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/career-clarity-reports')} className="gap-2 bg-white">
            <FileText className="h-4 w-4" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-blue-400 bg-blue-50 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Stop Guessing. Know Exactly What Jobs Fit You Best.
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed px-[8px]">
                Instead of applying blindly based on job titles, this report analyzes your complete professional 
                background and matches it against <span className="text-primary font-bold">15-20 job families</span> to 
                show exactly where you fit, where you're a stretch, and where you shouldn't waste your time. 
                Get real market intelligence: <span className="font-bold">location-specific salary data</span>, 
                actual job availability, and your <span className="text-primary font-bold">Top 25 Best-Match Companies</span>.
              </p>
            </div>

            {/* Two Info Links */}
            <div className="flex items-center justify-center gap-6 mt-4 mb-8">
              {/* Link 1: Why you need this (Amber themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Why you need this
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="w-5 h-5" />
                      Why You Need Career Clarity Before Applying
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Section 1: The Blind Application Trap */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">The Blind Application Trap</h3>
                        <p className="text-sm text-amber-900/80 mb-3">
                          One of the biggest mistakes job seekers make is applying to roles without knowing if they 
                          are truly the right fit. Without clarity, you waste time chasing opportunities that don't 
                          match your skills, your experience, or even your long-term goals.
                        </p>
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-destructive">70%</div>
                            <div className="text-sm text-muted-foreground">of job seekers apply to roles they're not qualified for</div>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: What Career Clarity Changes */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">What Career Clarity Changes</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Every application becomes targeted, not random</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Every interview makes sense—you know why you're there</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Every step moves you closer to the future you want</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">No more frustration, rejection, and burnout from wrong-fit roles</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: The Question You Should Ask */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">The Question Every Job Seeker Should Ask</h3>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-blue-900 font-medium text-center italic">
                            "What jobs am I actually qualified for, and which ones should I prioritize?"
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3">
                          This report answers that question with verified data—showing you not just what you 
                          <em>could</em> do, but what you <em>should</em> pursue given your background, the market, and your goals.
                        </p>
                      </div>

                      <Separator />

                      {/* Section 4: Why Most Job Seekers Fail */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">Why Most Job Seekers Struggle</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Guessing which roles to pursue based on job titles alone</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">No visibility into actual salary ranges for their market</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Unknown competition levels for target roles</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Result: months wasted applying to wrong opportunities</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <span className="text-muted-foreground">|</span>

              {/* Link 2: What you'll discover (Blue themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-blue-600 hover:text-blue-700 underline decoration-blue-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    What you'll discover →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-blue-600">
                      <Compass className="w-5 h-5" />
                      What You'll Learn in Your Career Clarity Report
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Section 1: Career Fit Overview */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-800 mb-2">Career Fit Overview</h3>
                        <p className="text-sm text-blue-900/80">
                          A scored ranking of <span className="font-bold">15-20 job families</span> showing fit percentage (0-100), 
                          why you qualify based on your specific experience and skills, what each role actually does 
                          day-to-day, and any gaps you'd need to close.
                        </p>
                      </div>

                      {/* Section 2: Top Role Deep Dives */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">Top Role Deep Dives</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Detailed analysis of your 5-7 best-fit roles</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Exactly which parts of your background match</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">What's missing and how to address it</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Recruiter alias titles so you don't miss relevant postings</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: Top 25 Companies */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          Top 25 Best-Match Companies
                        </h3>
                        <p className="text-sm text-green-900/80 mb-2">
                          Curated list of companies in your target location that align with your experience, 
                          industry background, and career level—based on:
                        </p>
                        <div className="space-y-1 text-sm text-green-900/80">
                          <p>• Who's actively hiring for your matched roles</p>
                          <p>• Company size and culture fit</p>
                          <p>• Growth trajectory</p>
                          <p>• Employee sentiment from Glassdoor/Indeed reviews</p>
                        </div>
                      </div>

                      {/* Section 4: Salary Data */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-success" />
                          Location-Specific Salary Data
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Verified compensation ranges (25th/50th/75th percentiles) for your target market from 
                          Glassdoor, Payscale, BLS, and Levels.fyi—<strong>not generic national averages</strong>, 
                          but what employers in your area actually pay.
                        </p>
                      </div>

                      <Separator />

                      {/* Section 5: Live Job Availability */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Search className="w-4 h-4 text-primary" />
                          Live Job Availability
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Real-time job counts from Indeed for each role in your location</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Top companies actively hiring</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Direct links to actual postings you can apply to today</p>
                          </div>
                        </div>
                      </div>

                      {/* Section 6: Competition Context */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4 text-amber-600" />
                          Competition Context
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          How many applicants per posting in your target industries, referral vs. cold apply 
                          success rates, and which skills are undersupplied vs. oversaturated in the current market.
                        </p>
                      </div>

                      <Separator />

                      {/* Section 7: Gap Analysis & Recommendations */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Gap Analysis
                          </h3>
                          <p className="text-sm text-amber-900/80">
                            Honest assessment of where you fall short for stretch roles, with specific 
                            recommendations for certifications, skills, or experience that would unlock 
                            higher-fit opportunities.
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                            <Route className="w-4 h-4" />
                            Strategic Recommendations
                          </h3>
                          <p className="text-sm text-blue-900/80">
                            Your 2-3 primary focus roles, whether to go volume or targeted, which job boards 
                            matter for your field, and realistic timeline expectations.
                          </p>
                        </div>
                      </div>

                      {/* Section 8: Skill Development */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4" />
                          Skill Development Roadmap
                        </h3>
                        <p className="text-sm text-purple-900/80">
                          What to learn or certify to move from "qualified" to "highly competitive," 
                          prioritized by impact on your career trajectory.
                        </p>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>
            {/* Feature Boxes Inside Hero Card */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border-2 border-blue-600 rounded-xl p-5 text-center">
                <BarChart3 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-foreground">15-20 Job Families</div>
                <div className="text-sm text-muted-foreground">scored & ranked</div>
              </div>
              <div className="bg-white border-2 border-blue-600 rounded-xl p-5 text-center">
                <Building className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-foreground">Top 25 Companies</div>
                <div className="text-sm text-muted-foreground">matched to you</div>
              </div>
              <div className="bg-white border-2 border-blue-600 rounded-xl p-5 text-center">
                <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-foreground">Real Salary Data</div>
                <div className="text-sm text-muted-foreground">location-specific</div>
              </div>
              <div className="bg-white border-2 border-blue-600 rounded-xl p-5 text-center">
                <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-foreground">~5 Minutes</div>
                <div className="text-sm text-muted-foreground">to complete</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Section */}
        <Card className="border-2 border-blue-400 bg-white shadow-lg overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Compass className="w-5 h-5 text-blue-600" />
              Start Your Career Clarity Assessment
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Answer a few quick questions to get your personalized career clarity report
            </p>
          </div>
          <CardContent className="p-6 space-y-4">
            <div>
              <Input
                placeholder="Current job title or background"
                value={currentExperience}
                onChange={(e) => setCurrentExperience(e.target.value)}
                className="text-base py-3 border-gray-400"
              />
            </div>
            <div>
              <Textarea
                placeholder="Key skills and areas of expertise (e.g., project management, data analysis, customer service)"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                className="text-base min-h-20 border-gray-400"
              />
            </div>
            <div>
              <Textarea
                placeholder="Career goals and values (e.g., work-life balance, high growth potential, remote work)"
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                className="text-base min-h-20 border-gray-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={experience} onValueChange={setExperience}>
                <SelectTrigger className="border-gray-400">
                  <SelectValue placeholder="Years of experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-2">0-2 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="6-10">6-10 years</SelectItem>
                  <SelectItem value="11-15">11-15 years</SelectItem>
                  <SelectItem value="15+">15+ years</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Location or Remote"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="text-base py-3 border-gray-400"
              />
            </div>
            <div className="space-y-3 pt-2">
              <Button 
                onClick={handleStartAssessment}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 text-lg rounded-xl"
              >
                <Compass className="w-5 h-5 mr-2" />
                Complete Full Assessment
              </Button>
              <Button 
                onClick={handleQuickStart}
                variant="outline"
                className="w-full py-3 text-base border-blue-300 hover:bg-blue-50"
              >
                Quick Start with Current Info
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
